// The one place the browser talks to the API. Cookies ride along because the app and the API share
// an origin (the dev server proxies /api), and every change is sent as JSON, which the API requires.
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : undefined;
  if (!response.ok) {
    const problem = payload as { error?: string; code?: string } | undefined;
    throw new ApiError(response.status, problem?.error ?? `Request failed (${response.status})`, problem?.code);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  /** Multipart, for the catalog CSV and company logos. */
  async upload<T>(path: string, file: File, field = 'file'): Promise<T> {
    const form = new FormData();
    form.append(field, file);
    const response = await fetch(`/api${path}`, { method: 'POST', body: form });
    const text = await response.text();
    const payload = text ? (JSON.parse(text) as unknown) : undefined;
    if (!response.ok) {
      const problem = payload as { error?: string; code?: string } | undefined;
      throw new ApiError(response.status, problem?.error ?? 'Upload failed', problem?.code);
    }
    return payload as T;
  },
};

export const errorText = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong.');
